import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Copy, Check, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";

/**
 * AdminWebhookDrawer
 * Right-side drawer that displays integration details (tenant id, webhook URL,
 * secret key, public endpoint, example curl) for the selected tenant and
 * exposes a "rotate secret" action.
 *
 * Extracted from pages/Admin.jsx during the production-readiness pass.
 * No behavioural change.
 */
export default function AdminWebhookDrawer({ tenant, onClose }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [revealed, setRevealed] = useState(false);
    const [copied, setCopied] = useState(null);
    const [rotating, setRotating] = useState(false);
    const [justRotated, setJustRotated] = useState(false);

    useEffect(() => {
        if (!tenant) {
            setData(null);
            setRevealed(false);
            setJustRotated(false);
            return;
        }
        setLoading(true);
        (async () => {
            try {
                const { data } = await api.get(`/admin/tenants/${tenant.id}/webhook`);
                setData(data);
            } catch (_e) {
                setData(null);
            } finally {
                setLoading(false);
            }
        })();
    }, [tenant]);

    const copy = async (value, key) => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(key);
            toast("Copied", { duration: 1500 });
            setTimeout(() => setCopied(null), 1600);
        } catch (_e) {
            /* ignore */
        }
    };

    const rotate = async () => {
        if (!tenant) return;
        const confirmed = window.confirm(
            "Are you sure? This will disconnect their live website until updated."
        );
        if (!confirmed) return;
        setRotating(true);
        try {
            const { data: next } = await api.post(`/admin/tenants/${tenant.id}/webhook/rotate`);
            setData(next);
            setRevealed(true);
            setJustRotated(true);
            toast.success("Webhook key rotated. Update the client's site with the new key.", {
                duration: 3500,
            });
            setTimeout(() => setJustRotated(false), 6000);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to rotate key");
        } finally {
            setRotating(false);
        }
    };

    const maskedSecret = data
        ? data.secret.slice(0, 8) + "•".repeat(12) + data.secret.slice(-4)
        : "";

    return (
        <AnimatePresence>
            {tenant && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm"
                    onClick={onClose}
                    data-testid="admin-webhook-drawer-overlay"
                >
                    <motion.div
                        initial={{ y: 40, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 30, opacity: 0 }}
                        transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                        onClick={(e) => e.stopPropagation()}
                        className="fixed inset-x-0 bottom-0 sm:inset-y-0 sm:right-0 sm:left-auto sm:w-[520px] bg-[#0a0a0a] border-t sm:border-t-0 sm:border-l border-zinc-800 rounded-t-2xl sm:rounded-none flex flex-col max-h-[88vh] sm:max-h-none sm:h-full"
                        data-testid="admin-webhook-drawer"
                    >
                        <div className="flex items-start justify-between p-6 border-b border-zinc-800">
                            <div className="min-w-0">
                                <p className="text-[11px] uppercase tracking-[0.16em] text-zinc-500">Integration</p>
                                <h2
                                    className="mt-1 text-xl font-medium text-zinc-50 tracking-tight truncate"
                                    data-testid="admin-webhook-drawer-tenant"
                                >
                                    {tenant.business_name}
                                </h2>
                                <p className="mt-0.5 text-[12.5px] text-zinc-500 truncate">
                                    {tenant.owner_email}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={onClose}
                                data-testid="admin-webhook-drawer-close"
                                aria-label="Close"
                                className="h-8 w-8 inline-flex items-center justify-center rounded-md text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900 transition-colors"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6 space-y-6">
                            {loading && (
                                <p className="text-[13.5px] text-zinc-500">Loading integration details…</p>
                            )}
                            {!loading && !data && (
                                <p className="text-[13.5px] text-zinc-500">Could not load details.</p>
                            )}
                            {!loading && data && (
                                <>
                                    <div>
                                        <p className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 mb-2">
                                            Tenant ID
                                        </p>
                                        <div className="flex items-center gap-2">
                                            <code
                                                className="flex-1 min-w-0 text-[12.5px] text-zinc-200 font-mono bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2.5 truncate"
                                                data-testid="admin-tenant-id"
                                            >
                                                {data.tenant_id}
                                            </code>
                                            <button
                                                type="button"
                                                onClick={() => copy(data.tenant_id, "tenant")}
                                                data-testid="admin-copy-tenant-id"
                                                aria-label="Copy tenant ID"
                                                className="shrink-0 h-10 w-10 inline-flex items-center justify-center rounded-md border border-zinc-800 text-zinc-400 hover:text-zinc-100 hover:border-zinc-700 transition-colors"
                                            >
                                                {copied === "tenant" ? (
                                                    <Check className="h-4 w-4 text-emerald-400" />
                                                ) : (
                                                    <Copy className="h-4 w-4" />
                                                )}
                                            </button>
                                        </div>
                                    </div>

                                    <div>
                                        <p className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 mb-2">
                                            Webhook URL (lead intake)
                                        </p>
                                        <div className="flex items-center gap-2">
                                            <code
                                                className="flex-1 min-w-0 text-[12.5px] text-zinc-200 font-mono bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2.5 truncate"
                                                data-testid="admin-webhook-url"
                                            >
                                                {data.webhook_url}
                                            </code>
                                            <button
                                                type="button"
                                                onClick={() => copy(data.webhook_url, "url")}
                                                data-testid="admin-webhook-copy-url"
                                                aria-label="Copy URL"
                                                className="shrink-0 h-10 w-10 inline-flex items-center justify-center rounded-md border border-zinc-800 text-zinc-400 hover:text-zinc-100 hover:border-zinc-700 transition-colors"
                                            >
                                                {copied === "url" ? (
                                                    <Check className="h-4 w-4 text-emerald-400" />
                                                ) : (
                                                    <Copy className="h-4 w-4" />
                                                )}
                                            </button>
                                        </div>
                                    </div>

                                    <div>
                                        <p className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 mb-2">
                                            Secret key
                                        </p>
                                        <div className="flex items-center gap-2">
                                            <code
                                                className="flex-1 min-w-0 text-[12.5px] text-zinc-200 font-mono bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2.5 truncate"
                                                data-testid="admin-webhook-secret"
                                            >
                                                {revealed ? data.secret : maskedSecret}
                                            </code>
                                            <button
                                                type="button"
                                                onClick={() => setRevealed((r) => !r)}
                                                data-testid="admin-webhook-reveal"
                                                className="shrink-0 h-10 px-3 inline-flex items-center justify-center rounded-md border border-zinc-800 text-[12px] text-zinc-400 hover:text-zinc-100 hover:border-zinc-700 transition-colors"
                                            >
                                                {revealed ? "Hide" : "Reveal"}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => copy(data.secret, "secret")}
                                                data-testid="admin-webhook-copy-secret"
                                                aria-label="Copy secret"
                                                className="shrink-0 h-10 w-10 inline-flex items-center justify-center rounded-md border border-zinc-800 text-zinc-400 hover:text-zinc-100 hover:border-zinc-700 transition-colors"
                                            >
                                                {copied === "secret" ? (
                                                    <Check className="h-4 w-4 text-emerald-400" />
                                                ) : (
                                                    <Copy className="h-4 w-4" />
                                                )}
                                            </button>
                                        </div>
                                        <p className="mt-2 text-[11.5px] text-zinc-600">
                                            Send as <code className="font-mono text-zinc-500">X-WebJesters-Key</code> on each request. Never share with the client.
                                        </p>
                                        <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
                                            {justRotated ? (
                                                <p
                                                    className="text-[11.5px] text-emerald-300/90"
                                                    data-testid="admin-rotate-rotated-note"
                                                >
                                                    New key generated. Old key is now invalid.
                                                </p>
                                            ) : (
                                                <p className="text-[11.5px] text-zinc-600">
                                                    Rotation immediately invalidates the previous key.
                                                </p>
                                            )}
                                            <button
                                                type="button"
                                                onClick={rotate}
                                                disabled={rotating}
                                                data-testid="admin-webhook-rotate"
                                                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-amber-900/50 bg-amber-950/30 text-amber-200 text-[12px] hover:border-amber-700/60 hover:bg-amber-950/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {rotating ? (
                                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                ) : (
                                                    <RefreshCw className="h-3.5 w-3.5" />
                                                )}
                                                Rotate key
                                            </button>
                                        </div>
                                    </div>

                                    <div>
                                        <p className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 mb-2">
                                            Public tenant endpoint (read-only, no auth)
                                        </p>
                                        <div className="flex items-center gap-2">
                                            <code
                                                className="flex-1 min-w-0 text-[12.5px] text-zinc-200 font-mono bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2.5 truncate"
                                                data-testid="admin-public-url"
                                            >
                                                {data.public_url}
                                            </code>
                                            <button
                                                type="button"
                                                onClick={() => copy(data.public_url, "public")}
                                                data-testid="admin-copy-public-url"
                                                aria-label="Copy public URL"
                                                className="shrink-0 h-10 w-10 inline-flex items-center justify-center rounded-md border border-zinc-800 text-zinc-400 hover:text-zinc-100 hover:border-zinc-700 transition-colors"
                                            >
                                                {copied === "public" ? (
                                                    <Check className="h-4 w-4 text-emerald-400" />
                                                ) : (
                                                    <Copy className="h-4 w-4" />
                                                )}
                                            </button>
                                        </div>
                                        <p className="mt-2 text-[11.5px] text-zinc-600">
                                            The Master Shell fetches this on page load. Cached for 60s.
                                        </p>
                                    </div>

                                    <div>
                                        <div className="flex items-center justify-between mb-2">
                                            <p className="text-[11px] uppercase tracking-[0.16em] text-zinc-500">
                                                Quick test
                                            </p>
                                            <button
                                                type="button"
                                                onClick={() => copy(data.example_curl, "curl")}
                                                data-testid="admin-webhook-copy-curl"
                                                className="text-[11.5px] text-zinc-500 hover:text-zinc-100 transition-colors inline-flex items-center gap-1"
                                            >
                                                {copied === "curl" ? (
                                                    <Check className="h-3 w-3 text-emerald-400" />
                                                ) : (
                                                    <Copy className="h-3 w-3" />
                                                )}
                                                Copy curl
                                            </button>
                                        </div>
                                        <pre
                                            className="text-[11.5px] text-zinc-300 font-mono bg-zinc-950 border border-zinc-800 rounded-md p-3 overflow-x-auto leading-relaxed whitespace-pre"
                                            data-testid="admin-webhook-example"
                                        >
                                            {data.example_curl}
                                        </pre>
                                    </div>
                                </>
                            )}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
